import { useEffect, useMemo, useState } from 'react'

import type { AssignToOption } from './AssignToModal'

export const UNASSIGN_OPTION_VALUE = 'unassign'

export interface UseAssignToModalParams {
  open: boolean
  options: AssignToOption[]
  assignedTo?: string
  isLoadingOptions?: boolean
  isError?: boolean
  isSubmitting?: boolean
  onAssign: (assigneeId: string, reviewConfirmed: boolean) => void
  onUnassign: () => void
  /** Acting manager id. Self-assign cannot record a peer-review confirmation. */
  actingUserId?: string
}

export interface UseAssignToModalReturn {
  userId: string | undefined
  setUserId: (value: string) => void
  userOptions: AssignToOption[]
  isReviewConfirmed: boolean
  setIsReviewConfirmed: (value: boolean) => void
  selectedMemberLabel: string | undefined
  isReviewConfirmationVisible: boolean
  submitLabel: string
  handleSave: () => void
  isSaveDisabled: boolean
}

const useAssignToModal = ({
  open,
  options,
  assignedTo,
  isLoadingOptions = false,
  isError = false,
  isSubmitting = false,
  onAssign,
  onUnassign,
  actingUserId
}: UseAssignToModalParams): UseAssignToModalReturn => {
  const [userId, setUserId] = useState<string | undefined>(assignedTo)
  const [isReviewConfirmed, setIsReviewConfirmed] = useState(false)

  // Re-sync the selection whenever the modal (re)opens so callers that keep it mounted and
  // only toggle `open` don't carry a stale selection over from a previous cancel.
  useEffect(() => {
    if (open) {
      setUserId(assignedTo)
      setIsReviewConfirmed(false)
    }
  }, [open, assignedTo])

  const userOptions = useMemo(() => {
    if (assignedTo) {
      return [{ label: 'Unassign', value: UNASSIGN_OPTION_VALUE }, ...options]
    }

    return options
  }, [options, assignedTo])

  const selectedMemberLabel = useMemo(
    () => userOptions.find(option => option.value === userId && option.value !== UNASSIGN_OPTION_VALUE)?.label,
    [userOptions, userId]
  )

  const isSelfAssign = Boolean(actingUserId && userId && userId === actingUserId)
  const isReviewConfirmationVisible = !isSelfAssign
  const reviewConfirmed = isReviewConfirmationVisible && isReviewConfirmed

  /**
   * The action follows the reviewer's intent rather than being a generic save: ticking the
   * confirmation turns a reassignment into a hand-off for peer review.
   */
  const submitLabel = useMemo(() => {
    if (userId === UNASSIGN_OPTION_VALUE) return 'Unassign'

    return reviewConfirmed ? 'Assign For Peer Review' : 'Assign'
  }, [userId, reviewConfirmed])

  const handleSave = (): void => {
    if (userId === UNASSIGN_OPTION_VALUE) {
      onUnassign()

      return
    }

    if (userId) {
      onAssign(userId, reviewConfirmed)
    }
  }

  const isSaveDisabled = !userId || isSubmitting || isLoadingOptions || isError

  return {
    userId,
    setUserId,
    userOptions,
    isReviewConfirmed,
    setIsReviewConfirmed,
    selectedMemberLabel,
    isReviewConfirmationVisible,
    submitLabel,
    handleSave,
    isSaveDisabled
  }
}

export default useAssignToModal
